import '../../App.css';
import axios from 'axios';
import { useEffect, useState, useRef } from 'react';
import Highlightable from 'highlightable';
import React from 'react';
import SelectionToolbar from '../SelectionToolbar';
import { logHighlightEvent } from '../../logging';
import { ProgressCircle, Tooltip } from '@adobe/react-spectrum';

function TweakingTimeDelayDocument({ ...properties }) {
    const [mouseWidgetInfo, setMouseWidgetInfo] = useState({ x: 0, y: 0, show: false, value: 0 });
    const userNeedsToWait = useRef(false);
    const userWaitedLongEnough = useRef(false);
    const [previousTextSelection, setPreviousTextSelection] = useState(null);
    const [mouseIsDown, setMouseIsDown] = useState(false);
    const mouseWidgetDisplayTimerRef = useRef(null);
    const mouseWidgetValueTimerRef = useRef(null);
    const [mouseWidgetColor, setMouseWidgetColor] = useState("var(--spectrum-red-900)");
    const [mouseWidgetInstructions, setMouseWidgetInstructions] = useState("Hold down to fill the gauge!");
    const [dummyWordCount, setDummyWordCount] = useState(0);
    const [tooltipOffset, setTooltipOffset] = useState({ x: 0, y: 0 });
    const tooltipRef = useRef(null);
    const mouseWidgetRef = useRef(null);
    const [mouseWidgetBounds, setMouseWidgetBounds] = useState({});
    const mouseDownPosition = useRef({ x: 0, y: 0, timestamp: 0 });
    const latestMousePositionRef = useRef({ x: 0, y: 0, timestamp: 0 })
    const potentialWordCount = useRef(0);

    function checkOverlappingRanges(left, right) {
        // the two ranges overlap
        if (right.start <= left.end) {
            return true
        }
        return false;
    }

    function mergeOverlappingRanges(left, right) {
        var newStart = Math.min(left.start, right.start);
        var newEnd = Math.max(left.end, right.end);
        var highlight = true;
        var showToolbar = false;

        // check whether the content should be highlighted right away
        if (properties.selectedTool !== "highlighter") {
            highlight = false;
            showToolbar = true;
        }
        return { start: newStart, end: newEnd, text: properties.passage.substring(newStart, newEnd + 1), highlight: highlight, showToolbar: showToolbar };
    }

    function mergeAllHighlights(newHighlight) {
        var highlights = properties.highlights.concat(newHighlight).sort((a, b) => a.start - b.start);

        function addDataToHighlight(highlightToEdit) {
            var highlight = true;
            var showToolbar = false;

            // check whether the content should be highlighted right away
            if (properties.selectedTool !== "highlighter") {
                highlight = false;
                showToolbar = true;
            }
            highlightToEdit.highlight = highlight;
            highlightToEdit.showToolbar = showToolbar;
        }

        for (var i = 0; i < highlights.length - 1; i++) {
            var left = highlights[i];
            var right = highlights[i + 1];
            if (checkOverlappingRanges(left, right)) {
                var merged = mergeOverlappingRanges(left, right);
                highlights.splice(i, 2, merged);
                i--;
            }
        }

        for (var i = 0; i < highlights.length; i++) {
            if (highlights[i].highlight === undefined || highlights[i].showToolbar === undefined) {
                addDataToHighlight(highlights[i]);
            }
        }


        return highlights;
    }

    function getHighlightedText(highlights) {
        var text = "";
        for (var i = 0; i < highlights.length; i++) {
            text += highlights[i].text + " ";
        }
        return text;
    }

    function checkRangeValid(range) {
        var rangeContents = document.getSelection().getRangeAt(0).cloneContents();
        var allSpans = rangeContents.querySelectorAll('span');
        var selectionSpans = [];
        allSpans.forEach((span) => {
            if (span.getAttribute('data-position') !== null) {
                selectionSpans.push(span);
            }
        });

        var startSpan = selectionSpans[0];
        var endSpan = selectionSpans[selectionSpans.length - 1];
        if (startSpan === undefined || endSpan === undefined) {
            return range;
        }

        // first check for any NaN values
        if (isNaN(range.start) || isNaN(range.end)) {
            var startIndex = parseInt(startSpan.getAttribute('data-position'));
            var endIndex = parseInt(endSpan.getAttribute('data-position'));
            range.start = startIndex;
            range.end = endIndex;
            range.text = properties.passage.substring(startIndex, endIndex + 1);
        }
        return range;
    }


    function onTextHighlighted() {
        // the user did not wait long enough so don't make the new highlight
        if (userNeedsToWait.current && !userWaitedLongEnough.current) {
            return;
        }

        var sel = document.getSelection();
        var unsnappedStartIndex = parseInt(sel.anchorNode.parentNode.getAttribute('data-position'));
        var unsnappedEndIndex = parseInt(sel.focusNode.parentNode.getAttribute('data-position'));
        var unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
        var unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

        let startNodeBounds = sel.anchorNode.parentNode.getBoundingClientRect().toJSON();
        let endNodeBounds = sel.focusNode.parentNode.getBoundingClientRect().toJSON();

        var wordSelection = snapSelectionToWord();
        var startNode = wordSelection.anchorNode.parentNode;
        var endNode = wordSelection.focusNode.parentNode;

        var startIndex = parseInt(startNode.getAttribute('data-position'));
        var endIndex = parseInt(endNode.getAttribute('data-position'));
        var leftToRight = startIndex < endIndex;
        // swap order if not left to right
        if (!leftToRight) {
            var tmpNode = endNode;
            endNode = startNode;
            startNode = tmpNode;

            var tmpIndex = endIndex;
            endIndex = startIndex;
            startIndex = tmpIndex;
        }

        var snappedRange = { start: startIndex, end: endIndex, text: properties.passage.substring(startIndex, endIndex + 1) };
        checkRangeValid(snappedRange);
        var mergedHighlights = mergeAllHighlights(snappedRange);
        var highlightedText = getHighlightedText(mergedHighlights);
        var newWordCount = properties.countNumWords(highlightedText);
        var validHighlight = true;

        if (newWordCount <= properties.maxWordCount || !properties.constrainHighlights) {
            // do not change the word count with the cursor tool, otherwise you double count
            if (properties.selectedTool === "highlighter") {
                properties.setWordCount(newWordCount);
            }
            properties.setHighlights(mergedHighlights);
            properties.setShowError(false);
            document.getSelection().removeAllRanges();
            validHighlight = true;
        }

        else if (newWordCount > properties.maxWordCount) {
            properties.setShowError(true);
            validHighlight = false;
        }

        let mousePosition = { mouseDown: mouseDownPosition.current, latestMousePosition: latestMousePositionRef.current };

        var log = {
            type: "add",
            timestamp: Date.now(),
            condition: properties.condition,
            experiment: properties.experiment,
            workerId: properties.workerId,
            participantId: properties.participantId,
            pass: properties.pass,

            tweakedParameter: properties.timeDelay,
            selectedLevel: properties.selectedLevel,
            mousePosition: mousePosition,
            startNodeBounds: startNodeBounds,
            endNodeBounds: endNodeBounds,

            unsnappedRange: unsnappedRange,
            snappedRange: snappedRange,
            newWordCount: newWordCount,
        }

        logHighlightEvent(log);
    }

    // helper function to snap the selection to words - https://stackoverflow.com/questions/7380190/select-whole-word-with-getselection
    function snapSelectionToWord() {
        var sel;

        // Check for existence of window.getSelection() and that it has a
        // modify() method. IE 9 has both selection APIs but no modify() method.
        if (window.getSelection && (sel = window.getSelection()).modify) {
            sel = window.getSelection();
            if (!sel.isCollapsed) {
                // Detect if selection is backwards
                var range = document.createRange();
                range.setStart(sel.anchorNode, sel.anchorOffset);
                range.setEnd(sel.focusNode, sel.focusOffset);
                var backwards = range.collapsed;
                range.detach();

                // modify() works on the focus of the selection
                var endNode = sel.focusNode,
                    endOffset = sel.focusOffset;
                sel.collapse(sel.anchorNode, sel.anchorOffset);

                var direction = [];
                if (backwards) {
                    direction = ['backward', 'forward'];
                } else {
                    direction = ['forward', 'backward'];
                }

                sel.modify("move", direction[0], "character");
                sel.modify("move", direction[1], "word");
                sel.extend(endNode, endOffset);
                sel.modify("extend", direction[1], "character");
                sel.modify("extend", direction[0], "word");

            }
        } else if ((sel === document.selection) && sel.type !== "Control") {
            var textRange = sel.createRange();
            if (textRange.text) {
                textRange.expand("word");
                // Move the end back to not include the word's trailing space(s),
                // if necessary
                while (/\s$/.test(textRange.text)) {
                    textRange.moveEnd("character", -1);
                }
                textRange.select();
            }
        }

        return sel;
    }

    function filterSelections(element) {
        return element.highlight;
    }
    // helper function to count the number of potential words after a selection, without double counting existing highlights
    function countNumPotentialWords() {
        var sel = document.getSelection();
        var unsnappedStartIndex = parseInt(sel.anchorNode.parentNode.getAttribute('data-position'));
        var unsnappedEndIndex = parseInt(sel.focusNode.parentNode.getAttribute('data-position'));
        var unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
        var unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

        var wordSelection = snapSelectionToWord();
        var startNode = wordSelection.anchorNode.parentNode;
        var endNode = wordSelection.focusNode.parentNode;

        var startIndex = parseInt(startNode.getAttribute('data-position'));
        var endIndex = parseInt(endNode.getAttribute('data-position'));
        var leftToRight = startIndex < endIndex;
        // swap order if not left to right
        if (!leftToRight) {
            var tmpNode = endNode;
            endNode = startNode;
            startNode = tmpNode;

            var tmpIndex = endIndex;
            endIndex = startIndex;
            startIndex = tmpIndex;
        }

        var snappedRange = { start: startIndex, end: endIndex, text: properties.passage.substring(startIndex, endIndex + 1), blurAmount: 0 };
        checkRangeValid(snappedRange);
        var mergedHighlights = mergeAllHighlights(snappedRange);
        var highlightedText = getHighlightedText(mergedHighlights);
        var newWordCount = properties.countNumWords(highlightedText);

        return newWordCount;
    }

    // filter out the selections that are not highlighted
    function mouseDown(e) {
        if (e.target.className !== "blackButton") {
            // hide temporary highlights
            var filteredHighlights = properties.highlights.filter(filterSelections);

            // hide all black buttons
            for (var i = 0; i < filteredHighlights.length; i++) {
                filteredHighlights[i].showToolbar = false;
            }

            properties.setHighlights(filteredHighlights);
            properties.setShowError(false);
        }

        setMouseIsDown(true);
        mouseDownPosition.current = { x: e.clientX, y: e.clientY, timestamp: Date.now() };


    }

    function mouseUp(e) {
        setMouseIsDown(false);
        setMouseWidgetInfo(prev => ({ ...prev, show: false }));
        // setMouseWidgetInfo({ x: 0, y: 0, show: false, value: 0 });
        clearInterval(mouseWidgetValueTimerRef.current);

        if (mouseWidgetInfo.value >= (properties.timeDelay * 1000)) {
            userWaitedLongEnough.current = true;
        } else {
            userWaitedLongEnough.current = false;

            // log the highlight attempt
            let sel = document.getSelection();
            let unsnappedStartIndex = parseInt(sel.anchorNode.parentNode.getAttribute('data-position'));
            let unsnappedEndIndex = parseInt(sel.focusNode.parentNode.getAttribute('data-position'));
            let unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
            let unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };
            let startNodeBounds = sel.anchorNode.parentNode.getBoundingClientRect().toJSON();
            let endNodeBounds = sel.focusNode.parentNode.getBoundingClientRect().toJSON();
            let mousePosition = { mouseDown: mouseDownPosition.current, mouseQuit: { x: e.clientX, y: e.clientY, timestamp: Date.now() } };
            let log = {
                type: "quit",
                timestamp: Date.now(),
                workerId: properties.workerId,
                participantId: properties.participantId,
                condition: properties.condition,
                experiment: properties.experiment,
                pass: properties.pass,
                tweakedParameter: properties.timeDelay,
                selectedLevel: properties.selectedLevel,
                unsnappedRange: unsnappedRange,
                startNodeBounds: startNodeBounds,
                endNodeBounds: endNodeBounds,
                mousePosition: mousePosition
            }

            logHighlightEvent(log);
        }
    }

    function mapRange(value, originalMin, originalMax, newMin, newMax) {
        let mappedValue = newMin + ((value - originalMin) * (newMax - newMin)) / (originalMax - originalMin);
        mappedValue = Math.min(newMax, Math.max(newMin, mappedValue));
        return mappedValue;
    }

    function mouseMove(e) {
        latestMousePositionRef.current = { x: e.clientX, y: e.clientY, timestamp: Date.now() };
        let textSelection = document.getSelection().toString();

        // determine if they need to wait at all - when tweaking, the widget should only show up if the delay is greater than 0
        if (textSelection.length > 0 && textSelection !== "") {
            // the participant picked a delay greater than 0, so show the mouse widget
            if (properties.timeDelay > 0) {
                userNeedsToWait.current = true;
            }

            else {
                setMouseWidgetInfo({ x: e.clientX + 10, y: e.clientY - 20, show: false, value: 0 });
                clearTimeout(mouseWidgetDisplayTimerRef.current);
                clearInterval(mouseWidgetValueTimerRef.current);
                userWaitedLongEnough.current = false;
                userNeedsToWait.current = false;
                return;
            }

        }


        // if the text selection is the same as the previous text selection, keep showing the mouse widget
        if (mouseWidgetInfo.show && textSelection === previousTextSelection) {
            setMouseWidgetInfo({ x: e.clientX + 10, y: e.clientY - 20, show: true, value: mouseWidgetInfo.value });
        }

        // the user is still selecting text, still show the mouse widget if it's more than the allocated word count but reset it
        else if (mouseWidgetInfo.show && textSelection !== previousTextSelection) {
            setMouseWidgetInfo({ x: e.clientX + 10, y: e.clientY - 20, show: true, value: 0 });
            clearInterval(mouseWidgetValueTimerRef.current);
            userWaitedLongEnough.current = false;
        }

        // Clear any existing timer
        if (mouseWidgetDisplayTimerRef.current) {
            clearTimeout(mouseWidgetDisplayTimerRef.current);
            userWaitedLongEnough.current = false;
        }

        if (textSelection.length > 0 && mouseIsDown) {
            // Update the mouse widget position
            setMouseWidgetInfo({ x: e.clientX + 10, y: e.clientY - 20, show: true, value: 0 });

            // Clear any existing interval
            if (mouseWidgetValueTimerRef.current) {
                clearInterval(mouseWidgetValueTimerRef.current);
            }

            // update the value of the mouse widget
            mouseWidgetValueTimerRef.current = setInterval(() => {
                setMouseWidgetInfo(prev => ({ ...prev, value: prev.value + 100 }));
            }, 100);
        }


        // Update the previous text selection
        setPreviousTextSelection(textSelection);

        return () => {
            clearTimeout(mouseWidgetDisplayTimerRef.current);
            clearInterval(mouseWidgetValueTimerRef.current);
            userWaitedLongEnough.current = false;
        };
    }

    useEffect(() => {
        if (tooltipRef.current && mouseWidgetRef.current) {
            let tooltip = tooltipRef.current.UNSAFE_getDOMNode();
            let tooltipBounds = tooltip.getBoundingClientRect();
            setTooltipOffset({ x: (tooltipBounds.width / 2) - (mouseWidgetBounds.width / 2), y: tooltipBounds.height - mouseWidgetBounds.height / 2 + 15 });
        }
    }, [mouseWidgetInstructions, mouseWidgetBounds]);

    useEffect(() => {
        let mouseWidget = mouseWidgetRef.current.UNSAFE_getDOMNode();
        let mouseWidgetBounds = mouseWidget.getBoundingClientRect();
        if (mouseWidgetBounds.width > 0 && mouseWidgetBounds.height > 0) {
            setMouseWidgetBounds(mouseWidget.getBoundingClientRect());
        }


        if (mouseWidgetInfo.value >= (properties.timeDelay * 1000)) {
            setMouseWidgetColor("var(--spectrum-green-600)");
            setMouseWidgetInstructions("Release to highlight!");
        }
        else {
            setMouseWidgetColor("var(--spectrum-red-800)");
            setMouseWidgetInstructions("Hold down to fill the gauge!");
        }
    }, [mouseWidgetInfo]);

    // listen to scrolling to hide the error message
    useEffect(() => {
        const onScroll = (event) => console.info("scrolling", event);

        window.addEventListener('scroll', mouseScroll);

        return () => {
            window.removeEventListener('scroll', mouseScroll);
        }
    }, []);

    function mouseScroll(e) {
        properties.setShowError(false);
    }

    return (
        <>
            <Tooltip UNSAFE_className="no-select" ref={tooltipRef} isOpen={mouseWidgetInfo.show} left={mouseWidgetInfo.x - tooltipOffset.x} top={mouseWidgetInfo.y - tooltipOffset.y} placement='top' position={"fixed"} zIndex={10000}>{mouseWidgetInstructions}</Tooltip>
            <ProgressCircle ref={mouseWidgetRef} isHidden={!mouseWidgetInfo.show} UNSAFE_style={{ "--spectrum-loader-circle-medium-track-fill-color": mouseWidgetColor }} left={mouseWidgetInfo.x} top={mouseWidgetInfo.y} size='S' position={"fixed"} zIndex={10000} aria-label='Wait to select.' value={(mouseWidgetInfo.value / (properties.timeDelay * 1000)) * 100} />

            <div id="textToRead" className='tweakingDocumentReader' onMouseDown={e => mouseDown(e)} onCopy={e => e.preventDefault()} onMouseUp={e => mouseUp(e)} onMouseMove={e => mouseMove(e)}>
                <div style={{ position: "fixed", left: mouseWidgetInfo.x, top: mouseWidgetInfo.y, zIndex: 10000 }}>
                </div>

                <Highlightable
                    ranges={properties.highlights}
                    enabled={properties.maxWordCount === 0 ? false : true} // disable highlighting if the max word count is 0
                    highlightStyle={range => {
                        if (range.highlight) {
                            return {
                                backgroundColor: '#FDEAA3',
                            }
                        }
                        else {
                            return {
                                backgroundColor: '#b3d8ff',
                            }
                        }
                    }}
                    onTextHighlighted={onTextHighlighted}
                    text={properties.passage}
                    nodeRenderer={(index, range, text) => {
                        var currChar = text.charAt(index);
                        if (currChar === '\n') {
                            return (<><br /><br /></>)
                        }
                        return (
                            <span data-position={index} range={range}>{currChar}</span>
                        )
                    }}
                    rangeRenderer={range => {
                        // helper function to update the tool bar visibility
                        function updateShowToolbar(newValue) {
                            var index = properties.highlights.indexOf(range[0].props.range);
                            var newHighlight = properties.highlights[index];
                            newHighlight.showToolbar = newValue;
                            properties.setHighlights(properties.highlights.toSpliced(index, 1).concat(newHighlight));
                        }

                        function mouseEnter(e) {
                            e.style.outline = "2px solid #337AC2";
                            e.style.cursor = "pointer";
                        }

                        function mouseLeave(e) {
                            if (range[0].props.range.highlight)
                                e.style.outline = "2px solid #FDEAA3";
                            else
                                e.style.outline = "2px solid #b3d8ff";
                            e.style.cursor = "auto";
                        }

                        function mouseClick(e) {
                            updateShowToolbar(true);
                        }

                        // creating an actual highlight
                        if (range[0].props.range.highlight) {
                            return (
                                <div style={{ display: 'inline', position: 'relative' }}>
                                    <SelectionToolbar
                                        passage={properties.passage}
                                        countNumWords={properties.countNumWords}

                                        wordCount={properties.wordCount}
                                        setWordCount={properties.setWordCount}
                                        setProgressWordCount={properties.setProgressWordCount}

                                        selectedTool={'highlighter'}
                                        updateShowToolbar={updateShowToolbar}
                                        workerId={properties.workerId}
                                        participantId={properties.participantId}
                                        condition={properties.condition}
                                        experiment={properties.experiment}
                                        pass={properties.pass}
                                        tweakedParameter={properties.timeDelay}

                                        range={range}
                                        highlights={properties.highlights}
                                        setHighlights={properties.setHighlights} />
                                    <span style={{ outline: "2px solid #FDEAA3", backgroundColor: "#FDEAA3" }} onMouseEnter={e => mouseEnter(e.target.parentNode)} onMouseLeave={e => mouseLeave(e.target.parentNode)} onClick={e => mouseClick(e.target.parentNode)}>{range}</span>
                                </div>
                            );

                        }

                        // creating a fake selection effect
                        else {
                            return (
                                <div style={{ display: 'inline', position: 'relative' }}>
                                    <SelectionToolbar
                                        passage={properties.passage}
                                        countNumWords={properties.countNumWords}

                                        wordCount={properties.wordCount}
                                        setWordCount={properties.setWordCount}
                                        setProgressWordCount={properties.setProgressWordCount}
                                        setShadowWordCount={properties.setShadowWordCount}

                                        selectedTool={'cursor'}
                                        updateShowToolbar={updateShowToolbar}

                                        range={range}

                                        highlights={properties.highlights}
                                        setHighlights={properties.setHighlights} />
                                    <span style={{ outline: "2px solid #b3d8ff", backgroundColor: "#b3d8ff" }}>{range}</span>
                                </div>)
                                ;
                        }
                    }}
                />


            </div>
        </>
    );
}

export default TweakingTimeDelayDocument;